import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Pagination,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DevicesIcon from '@mui/icons-material/Devices';
import DashboardIcon from '@mui/icons-material/Dashboard';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as ChartTooltip,
} from 'recharts';
import { useApi } from '../contexts/ApiContext';
import { subDays, format } from 'date-fns';

const COLORS = ['#f44336', '#ff9800', '#ffc107', '#03a9f4', '#4caf50', '#9c27b0', '#e91e63', '#00bcd4'];

const ErrorCard = ({ error, rank, total }) => {
  const percentage = (parseInt(error.total_occurrences) / total * 100).toFixed(1);
  const isReversal = error.resp_desc?.toLowerCase().includes('reversal') ||
                     error.resp_desc?.toLowerCase().includes('customer cancellation');

  return (
    <Card
      sx={{
        height: '100%',
        background: isReversal
          ? `linear-gradient(135deg, #ff1744 15%, #ff5252 100%)`
          : `linear-gradient(135deg, ${COLORS[rank % COLORS.length]}15 0%, ${COLORS[rank % COLORS.length]}05 100%)`,
        border: isReversal
          ? '2px solid #ff1744'
          : `2px solid ${COLORS[rank % COLORS.length]}40`,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Chip
            label={`#${rank + 1}`}
            size="small"
            sx={{
              bgcolor: isReversal ? '#ff1744' : COLORS[rank % COLORS.length],
              color: 'white',
              fontWeight: 'bold',
            }}
          />
          {isReversal && (
            <Chip
              label="REVERSAL"
              size="small"
              color="error"
              sx={{ ml: 1 }}
            />
          )}
          <ErrorIcon sx={{
            color: isReversal ? '#ff1744' : COLORS[rank % COLORS.length],
            fontSize: 32
          }} />
        </Box>

        <Typography
          variant="h3"
          fontWeight="bold"
          color={isReversal ? '#ff1744' : COLORS[rank % COLORS.length]}
          gutterBottom
        >
          {error.ext_resp_code || 'RWT'}
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
          {error.resp_desc || 'Unknown Error'}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 1 }}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="textSecondary">
              Occurrences
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {parseInt(error.total_occurrences).toLocaleString()} ({percentage}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={parseFloat(percentage)}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: isReversal ? '#ff174420' : `${COLORS[rank % COLORS.length]}20`,
              '& .MuiLinearProgress-bar': {
                bgcolor: isReversal ? '#ff1744' : COLORS[rank % COLORS.length],
              }
            }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Reversals
            </Typography>
            <Typography variant="body1" fontWeight="bold" color={isReversal ? 'error' : 'inherit'}>
              {parseInt(error.total_reversals).toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Terminals
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {parseInt(error.affected_terminals || 0).toLocaleString()}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="textSecondary">
              Total Amount
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="error">
              ${(parseFloat(error.total_amount || 0) / 100).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SummaryCard = ({ title, value, subtitle, icon, color }) => (
  <Card>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color}>
            {value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default function TransactionTroubleshooting() {
  const { apiCall } = useApi();
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(subDays(new Date(), 1));
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [terminalErrors, setTerminalErrors] = useState([]);
  const [locationErrors, setLocationErrors] = useState([]);
  // Sorting state for terminals table
  const [terminalOrder, setTerminalOrder] = useState('asc');
  const [terminalOrderBy, setTerminalOrderBy] = useState('total_errors');
  // Sorting state for locations table
  const [locationOrder, setLocationOrder] = useState('desc');
  const [locationOrderBy, setLocationOrderBy] = useState('total_errors');
  // Pagination state for terminals
  const [terminalPage, setTerminalPage] = useState(1);
  const [terminalLimit, setTerminalLimit] = useState(25);
  const [terminalPagination, setTerminalPagination] = useState(null);
  // Pagination state for locations
  const [locationPage, setLocationPage] = useState(1);
  const [locationLimit, setLocationLimit] = useState(25);
  const [locationPagination, setLocationPagination] = useState(null);

  // Sorting handler for terminals table
  const handleTerminalSort = (property) => {
    const isAsc = terminalOrderBy === property && terminalOrder === 'asc';
    setTerminalOrder(isAsc ? 'desc' : 'asc');
    setTerminalOrderBy(property);
  };

  // Sort terminals data
  const sortedTerminalErrors = React.useMemo(() => {
    // Ensure terminalErrors is an array before spreading
    if (!Array.isArray(terminalErrors)) {
      console.warn('terminalErrors is not an array:', terminalErrors);
      return [];
    }
    return [...terminalErrors].sort((a, b) => {
      let aVal = a[terminalOrderBy];
      let bVal = b[terminalOrderBy];

      // Handle numeric values
      if (terminalOrderBy === 'total_transactions' || terminalOrderBy === 'total_errors' ||
          terminalOrderBy === 'total_reversals' || terminalOrderBy === 'error_amount' ||
          terminalOrderBy === 'reversal_rate' || terminalOrderBy === 'failure_rate') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle string values
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) {
        return terminalOrder === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return terminalOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [terminalErrors, terminalOrder, terminalOrderBy]);

  // Sorting handler for locations table
  const handleLocationSort = (property) => {
    const isAsc = locationOrderBy === property && locationOrder === 'asc';
    setLocationOrder(isAsc ? 'desc' : 'asc');
    setLocationOrderBy(property);
  };

  // Sort locations data
  const sortedLocationErrors = React.useMemo(() => {
    // Ensure locationErrors is an array before spreading
    if (!Array.isArray(locationErrors)) {
      console.warn('locationErrors is not an array:', locationErrors);
      return [];
    }
    return [...locationErrors].sort((a, b) => {
      let aVal = a[locationOrderBy];
      let bVal = b[locationOrderBy];

      // Handle numeric values
      if (locationOrderBy === 'total_transactions' || locationOrderBy === 'total_errors' ||
          locationOrderBy === 'total_reversals' || locationOrderBy === 'error_amount' ||
          locationOrderBy === 'reversal_rate' || locationOrderBy === 'failure_rate' ||
          locationOrderBy === 'terminal_count') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle string values (location name, top_error)
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) {
        return locationOrder === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return locationOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [locationErrors, locationOrder, locationOrderBy]);

  useEffect(() => {
    fetchData();
  }, [dateFrom, dateTo, selectedTerminal, tabValue, terminalPage, terminalLimit, locationPage, locationLimit]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = {
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd'),
      };

      if (selectedTerminal) {
        params.terminal = selectedTerminal;
      }

      // Fetch data based on current tab
      let promises = [];

      if (tabValue === 0) {
        // Overview tab
        promises = [
          apiCall('/transaction-troubleshooting/errors', 'GET', null, params),
          apiCall('/transaction-troubleshooting/summary', 'GET', null, params)
        ];
        const [errorsResponse, summaryResponse] = await Promise.all(promises);
        setErrors(errorsResponse || []);
        setSummary(summaryResponse || {});
      } else if (tabValue === 1) {
        // Terminals tab - add pagination
        params.page = terminalPage;
        params.limit = terminalLimit;
        const response = await apiCall('/transaction-troubleshooting/terminals', 'GET', null, params);
        console.log('Terminals response:', response);
        // Handle both old format (array) and new format (object with data and pagination)
        if (response && typeof response === 'object' && 'data' in response) {
          // New paginated format
          setTerminalErrors(Array.isArray(response.data) ? response.data : []);
          setTerminalPagination(response.pagination || null);
        } else if (Array.isArray(response)) {
          // Old array format
          setTerminalErrors(response);
          setTerminalPagination(null);
        } else {
          // Unexpected format
          console.error('Unexpected terminals response format:', response);
          setTerminalErrors([]);
          setTerminalPagination(null);
        }
      } else if (tabValue === 2) {
        // Locations tab - add pagination
        params.page = locationPage;
        params.limit = locationLimit;
        const response = await apiCall('/transaction-troubleshooting/locations', 'GET', null, params);
        console.log('Locations response:', response);
        // Handle both old format (array) and new format (object with data and pagination)
        if (response && typeof response === 'object' && 'data' in response) {
          // New paginated format
          setLocationErrors(Array.isArray(response.data) ? response.data : []);
          setLocationPagination(response.pagination || null);
        } else if (Array.isArray(response)) {
          // Old array format
          setLocationErrors(response);
          setLocationPagination(null);
        } else {
          // Unexpected format
          console.error('Unexpected locations response format:', response);
          setLocationErrors([]);
          setLocationPagination(null);
        }
      }

      // Get unique terminals from errors for filter dropdown
      const uniqueTerminals = [...new Set(errors?.flatMap(e => e.affected_terminals) || [])];
      setTerminals(uniqueTerminals.sort());

    } catch (error) {
      console.error('Error fetching troubleshooting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Error Code', 'Description', 'Occurrences', 'Reversals', 'Affected Terminals', 'Total Amount', 'Average Amount'],
      ...errors.map(e => [
        e.ext_resp_code || 'RWT',
        e.resp_desc || '',
        e.total_occurrences,
        e.total_reversals,
        e.affected_terminals || 0,
        (parseFloat(e.total_amount || 0) / 100).toFixed(2),
        (parseFloat(e.avg_amount || 0)).toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-analysis-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const topErrors = errors.slice(0, 6);
  const totalOccurrences = errors.reduce((sum, e) => sum + parseInt(e.total_occurrences), 0);
  const totalReversals = errors.reduce((sum, e) => sum + parseInt(e.total_reversals), 0);
  const totalAmount = errors.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0);

  // Prepare data for pie chart
  const chartData = topErrors.map((error, index) => ({
    name: error.ext_resp_code || 'RWT',
    value: parseInt(error.total_occurrences),
    color: error.resp_desc?.toLowerCase().includes('reversal') ? '#ff1744' : COLORS[index % COLORS.length],
  }));

  const otherTotal = totalOccurrences - chartData.reduce((sum, item) => sum + item.value, 0);
  if (otherTotal > 0) {
    chartData.push({
      name: 'Others',
      value: otherTotal,
      color: '#bdbdbd',
    });
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Transaction Troubleshooting
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Error analysis and reversal patterns
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExportCSV} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="fullWidth">
          <Tab label="Overview" icon={<DashboardIcon />} iconPosition="start" />
          <Tab label="By Terminal" icon={<DevicesIcon />} iconPosition="start" />
          <Tab label="By Location" icon={<StorefrontIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      {tabValue === 0 && (
        <Alert severity="warning" icon={<TroubleshootIcon />} sx={{ mb: 3 }}>
          <strong>Focus on Reversals:</strong> Customer reversals (RWT) are highlighted in red as they are the #1 issue impacting transaction success rates.
        </Alert>
      )}

      {/* Date and Terminal Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateFrom}
                  onChange={setDateFrom}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To Date"
                  value={dateTo}
                  onChange={setDateTo}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              {tabValue === 0 && (
                <TextField
                  select
                  label="Terminal Filter"
                  value={selectedTerminal}
                  onChange={(e) => setSelectedTerminal(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">All Terminals</MenuItem>
                  {terminals.map(terminal => (
                    <MenuItem key={terminal} value={terminal}>
                      {terminal}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tab Content */}
      {tabValue === 0 && (
        <>
          {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Unique Error Codes"
            value={summary?.unique_error_codes || 0}
            subtitle={`${summary?.terminals_with_errors || 0} terminals affected`}
            icon={<ErrorIcon sx={{ fontSize: 32, color: '#f44336' }} />}
            color="#f44336"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Reversals"
            value={(summary?.total_reversals || 0).toLocaleString()}
            subtitle={`Out of ${(summary?.total_error_occurrences || 0).toLocaleString()} errors`}
            icon={<WarningIcon sx={{ fontSize: 32, color: '#ff9800' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Error-Related Amount"
            value={`$${((summary?.error_related_amount || 0) / 100).toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`}
            subtitle={`${summary?.terminal_error_rate || 0}% terminal error rate`}
            icon={<MoneyOffIcon sx={{ fontSize: 32, color: '#e91e63' }} />}
            color="#e91e63"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Top Error Cards */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h6" gutterBottom>
            Top Error Codes
          </Typography>
          <Grid container spacing={2}>
            {topErrors.map((error, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ErrorCard error={error} rank={index} total={totalOccurrences} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Error Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    formatter={(value) => value.toLocaleString()}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

          {/* All Errors List */}
          {errors.length > 6 && (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                All Error Codes ({errors.length})
              </Typography>
              <Grid container spacing={2}>
                {errors.map((error, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderColor: error.resp_desc?.toLowerCase().includes('reversal') ? '#ff1744' : 'divider'
                      }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Chip
                            label={error.ext_resp_code || 'RWT'}
                            color={error.resp_desc?.toLowerCase().includes('reversal') ? 'error' : 'default'}
                            size="small"
                          />
                          <Typography variant="caption" color="textSecondary">
                            #{index + 1}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1, minHeight: 32, fontSize: '0.85rem' }}>
                          {error.resp_desc || 'Unknown Error'}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Occurrences: <strong>{parseInt(error.total_occurrences).toLocaleString()}</strong>
                          </Typography>
                          <br />
                          <Typography variant="caption" color="textSecondary">
                            Reversals: <strong style={{ color: '#ff1744' }}>
                              {parseInt(error.total_reversals).toLocaleString()}
                            </strong>
                          </Typography>
                          <br />
                          <Typography variant="caption" color="textSecondary">
                            Amount: <strong>${(parseFloat(error.total_amount || 0) / 100).toLocaleString(undefined, {
                              minimumFractionDigits: 0
                            })}</strong>
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Terminals Tab */}
      {tabValue === 1 && (
        <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={terminalOrderBy === 'terminal_id'}
                    direction={terminalOrderBy === 'terminal_id' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('terminal_id')}
                  >
                    Terminal ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={terminalOrderBy === 'location'}
                    direction={terminalOrderBy === 'location' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'total_transactions'}
                    direction={terminalOrderBy === 'total_transactions' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('total_transactions')}
                  >
                    Total Transactions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'total_errors'}
                    direction={terminalOrderBy === 'total_errors' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('total_errors')}
                  >
                    Total Errors
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'total_reversals'}
                    direction={terminalOrderBy === 'total_reversals' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('total_reversals')}
                  >
                    Total Reversals
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'reversal_rate'}
                    direction={terminalOrderBy === 'reversal_rate' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('reversal_rate')}
                  >
                    Reversal Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'failure_rate'}
                    direction={terminalOrderBy === 'failure_rate' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('failure_rate')}
                  >
                    Failure Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'error_amount'}
                    direction={terminalOrderBy === 'error_amount' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('error_amount')}
                  >
                    Error Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={terminalOrderBy === 'top_error'}
                    direction={terminalOrderBy === 'top_error' ? terminalOrder : 'asc'}
                    onClick={() => handleTerminalSort('top_error')}
                  >
                    Top Error
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTerminalErrors.map((terminal, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Chip label={terminal.display_terminal_id || terminal.terminal_id} size="small" />
                  </TableCell>
                  <TableCell>{terminal.location || 'Unknown'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {parseInt(terminal.total_transactions || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {parseInt(terminal.total_errors || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography color={terminal.total_reversals > 0 ? 'error' : 'inherit'}>
                      {parseInt(terminal.total_reversals || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${parseFloat(terminal.reversal_rate || 0).toFixed(1)}%`}
                      size="small"
                      color={parseFloat(terminal.reversal_rate) > 5 ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={
                      terminal.failure_breakdown ? (
                        <Box>
                          <Typography variant="caption" display="block">
                            Do Not Honor: {terminal.failure_breakdown.do_not_honor || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Insufficient Funds: {terminal.failure_breakdown.insufficient_funds || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Invalid PIN: {terminal.failure_breakdown.invalid_pin || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Card Expired: {terminal.failure_breakdown.card_expired || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Exceed Limit: {terminal.failure_breakdown.exceed_limit || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Other: {terminal.failure_breakdown.other || 0}
                          </Typography>
                        </Box>
                      ) : 'No failure data'
                    }>
                      <Chip
                        label={`${parseFloat(terminal.failure_rate || 0).toFixed(1)}%`}
                        size="small"
                        color={parseFloat(terminal.failure_rate) > 10 ? 'error' : 'default'}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    ${(parseFloat(terminal.error_amount || 0) / 100).toLocaleString(undefined, {
                      minimumFractionDigits: 0
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {terminal.top_error || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Terminals Pagination Controls */}
        {terminalPagination && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((terminalPage - 1) * terminalLimit) + 1} to {Math.min(terminalPage * terminalLimit, terminalPagination.totalCount)} of {terminalPagination.totalCount} terminals
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={terminalLimit}
                  label="Per Page"
                  onChange={(e) => {
                    setTerminalLimit(e.target.value);
                    setTerminalPage(1); // Reset to first page
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={terminalPagination.totalPages}
              page={terminalPage}
              onChange={(event, value) => setTerminalPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </>
      )}

      {/* Locations Tab */}
      {tabValue === 2 && (
        <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={locationOrderBy === 'location'}
                    direction={locationOrderBy === 'location' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'terminal_count'}
                    direction={locationOrderBy === 'terminal_count' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('terminal_count')}
                  >
                    Terminal Count
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'total_transactions'}
                    direction={locationOrderBy === 'total_transactions' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('total_transactions')}
                  >
                    Total Transactions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'total_errors'}
                    direction={locationOrderBy === 'total_errors' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('total_errors')}
                  >
                    Total Errors
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'total_reversals'}
                    direction={locationOrderBy === 'total_reversals' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('total_reversals')}
                  >
                    Total Reversals
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'reversal_rate'}
                    direction={locationOrderBy === 'reversal_rate' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('reversal_rate')}
                  >
                    Reversal Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'failure_rate'}
                    direction={locationOrderBy === 'failure_rate' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('failure_rate')}
                  >
                    Failure Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'error_amount'}
                    direction={locationOrderBy === 'error_amount' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('error_amount')}
                  >
                    Error Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={locationOrderBy === 'top_error'}
                    direction={locationOrderBy === 'top_error' ? locationOrder : 'asc'}
                    onClick={() => handleLocationSort('top_error')}
                  >
                    Top Error
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLocationErrors.map((location, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {location.location || 'Unknown Location'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {parseInt(location.terminal_count || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {parseInt(location.total_transactions || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {parseInt(location.total_errors || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography color={location.total_reversals > 0 ? 'error' : 'inherit'}>
                      {parseInt(location.total_reversals || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${parseFloat(location.reversal_rate || 0).toFixed(1)}%`}
                      size="small"
                      color={parseFloat(location.reversal_rate) > 5 ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={
                      location.failure_breakdown ? (
                        <Box>
                          <Typography variant="caption" display="block">
                            Do Not Honor: {location.failure_breakdown.do_not_honor || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Insufficient Funds: {location.failure_breakdown.insufficient_funds || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Invalid PIN: {location.failure_breakdown.invalid_pin || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Card Expired: {location.failure_breakdown.card_expired || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Exceed Limit: {location.failure_breakdown.exceed_limit || 0}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Other: {location.failure_breakdown.other || 0}
                          </Typography>
                        </Box>
                      ) : 'No failure data'
                    }>
                      <Chip
                        label={`${parseFloat(location.failure_rate || 0).toFixed(1)}%`}
                        size="small"
                        color={parseFloat(location.failure_rate) > 10 ? 'error' : 'default'}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    ${(parseFloat(location.error_amount || 0) / 100).toLocaleString(undefined, {
                      minimumFractionDigits: 0
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {location.top_error || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Locations Pagination Controls */}
        {locationPagination && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((locationPage - 1) * locationLimit) + 1} to {Math.min(locationPage * locationLimit, locationPagination.totalCount)} of {locationPagination.totalCount} locations
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={locationLimit}
                  label="Per Page"
                  onChange={(e) => {
                    setLocationLimit(e.target.value);
                    setLocationPage(1); // Reset to first page
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={locationPagination.totalPages}
              page={locationPage}
              onChange={(event, value) => setLocationPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </>
      )}
    </Box>
  );
}